import React, { useState, useEffect } from 'react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, LineChart, Line, Legend 
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import { TrendingUp, BarChart2, PieChart as PieChartIcon, Activity, ThumbsUp, MessageSquare, Compass } from 'lucide-react';

const COLORS = ['#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#FFEDD5'];

const AnalyticsDashboard = ({ coachId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await analyticsAPI.getDashboard({ coach_id: coachId });
        setData(response.data);
      } catch (error) {
        console.error('Error fetching analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [coachId]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tangerine"></div>
    </div>
  );

  if (!data) return null;

  const extractArray = (field) => {
    if (Array.isArray(field)) return field;
    if (field && typeof field === 'object') {
      return field.stats || field.items || field.data || [];
    }
    return [];
  };

  const categories = extractArray(data.categories);
  const sessionTypes = extractArray(data.session_types);
  const emotionalTrends = extractArray(data.emotional_trends);
  const interventions = extractArray(data.interventions);

  return (
    <div className="space-y-6 mb-8">
      {/* Summary KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Ratings Card */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm flex items-center gap-4">
          <div className="p-4 bg-orange-50 dark:bg-orange-950/20 text-tangerine rounded-2xl">
            <MessageSquare className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Total Ratings</p>
            <h4 className="text-2xl font-black text-[var(--text-primary)] mt-1">{data.total_ratings || 0}</h4>
          </div>
        </div>

        {/* Favorability Score Card */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm flex items-center gap-4">
          <div className="p-4 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-500 rounded-2xl">
            <ThumbsUp className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Favorability Score</p>
            <h4 className="text-2xl font-black text-[var(--text-primary)] mt-1">
              {data.total_ratings > 0 ? `${data.favorability_score}%` : 'N/A'}
            </h4>
          </div>
        </div>

        {/* Next Step Clarity Card */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm flex items-center gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 text-blue-500 rounded-2xl">
            <Compass className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-bold text-[var(--text-secondary)] uppercase tracking-wider">Left With Next Steps</p>
            <h4 className="text-2xl font-black text-[var(--text-primary)] mt-1">
              {data.total_ratings > 0 ? `${data.next_step_percentage}%` : 'N/A'}
            </h4>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Categories Distribution */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <PieChartIcon className="w-5 h-5 text-tangerine" />
            <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">Top Categories</h3>
          </div>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={categories}
                  cx="50%"
                  cy="40%"
                  innerRadius={60}
                  outerRadius={75}
                  paddingAngle={5}
                  dataKey="count"
                  nameKey="label"
                >
                  {categories.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center" 
                  iconSize={10}
                  formatter={(value) => <span style={{ color: 'var(--text-secondary)', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}>{value}</span>}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Session Types */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <BarChart2 className="w-5 h-5 text-tangerine" />
            <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">Session Types</h3>
          </div>
          <div className="h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sessionTypes}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-light)" opacity={0.5} />
                <XAxis 
                  dataKey="label" 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <Tooltip 
                  cursor={{ fill: 'var(--tangerine)', opacity: 0.05 }}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Bar dataKey="count" fill="var(--tangerine)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Emotional Trends */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm lg:col-span-2">
          <div className="flex items-center gap-2 mb-6">
            <Activity className="w-5 h-5 text-tangerine" />
            <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">Overall Emotional Dynamics</h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={emotionalTrends}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border-light)" opacity={0.5} />
                <XAxis 
                  dataKey="date" 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10}
                  tick={{ fill: 'var(--text-secondary)' }}
                  tickFormatter={(str) => {
                    try {
                      return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    } catch (e) {
                      return str;
                    }
                  }}
                />
                <YAxis 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  domain={[0, 5]} 
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                  labelFormatter={(str) => {
                    try {
                      return new Date(str).toLocaleString();
                    } catch (e) {
                      return str;
                    }
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="var(--tangerine)" 
                  strokeWidth={3} 
                  dot={{ r: 4, fill: 'var(--tangerine)', strokeWidth: 2, stroke: 'var(--surface-white)' }}
                  activeDot={{ r: 6, strokeWidth: 0 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Interventions */}
        <div className="bg-[var(--surface-white)] p-6 rounded-3xl border border-[var(--border-light)] shadow-sm lg:col-span-2">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-tangerine" />
            <h3 className="text-sm font-bold text-[var(--text-primary)] uppercase tracking-wider">Coaching Interventions</h3>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={interventions} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-light)" opacity={0.5} />
                <XAxis type="number" hide />
                <YAxis 
                  dataKey="label" 
                  type="category" 
                  axisLine={false} 
                  tickLine={false} 
                  fontSize={10} 
                  width={140} 
                  tick={{ fill: 'var(--text-secondary)' }}
                />
                <Tooltip 
                  cursor={{ fill: 'var(--tangerine)', opacity: 0.05 }}
                  contentStyle={{ 
                    borderRadius: '12px', 
                    border: 'none', 
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'var(--surface-white)',
                    color: 'var(--text-primary)'
                  }}
                  itemStyle={{ color: 'var(--text-primary)' }}
                />
                <Bar dataKey="count" fill="var(--tangerine)" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
